import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reportService } from '../services/reportService';
import { ExclamationTriangleIcon, LightBulbIcon, EyeIcon } from '@heroicons/react/24/outline';
import Pagination from '../../../components/common/Pagination';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import ReportDetailModal from './ReportDetailModal';

const typeLabel = (type) => (type === 'suggestion' ? 'Suggestion' : 'Problème');

const typeBadgeClass = (type) =>
  type === 'suggestion'
    ? 'bg-blue-100 text-blue-800'
    : 'bg-red-100 text-red-800';

const userLabel = (user) => {
  if (!user) return 'Utilisateur supprimé';
  return `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.phone_number || 'Utilisateur';
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ReportList = () => {
  const [page, setPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['reports', page],
    queryFn: () => reportService.getReports({ page }),
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id) => reportService.markAsRead(id),
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['reports-unread-count'] });
    },
  });

  const handleView = (report) => {
    setSelectedReport(report);
    if (!report.read_at) {
      markAsReadMutation.mutate(report.id);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <div className="text-red-500">Erreur lors du chargement des signalements</div>;

  const reports = data?.data || [];
  const pagination = data?.meta || {};

  return (
    <div className="bg-white min-h-full shadow-md rounded-lg">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">Signalements</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Problèmes et suggestions envoyés par les utilisateurs depuis l'application mobile.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Utilisateur</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Message</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Envoyé le</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Statut</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase">Actions</th>
            </tr>
          </thead>
          {reports.length > 0 ? (
            <tbody className="bg-white divide-y divide-gray-200">
              {reports.map((report) => (
                <tr key={report.id} className={!report.read_at ? 'bg-red-50/50' : ''}>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${typeBadgeClass(report.type)}`}>
                      {report.type === 'suggestion' ? (
                        <LightBulbIcon className="h-3.5 w-3.5" />
                      ) : (
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                      )}
                      {typeLabel(report.type)}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-sm ${!report.read_at ? 'font-medium' : ''}`}>
                    {userLabel(report.user)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                    {report.message}
                  </td>
                  <td className="px-4 py-3 text-sm">{formatDateTime(report.created_at)}</td>
                  <td className="px-4 py-3 text-sm">
                    {report.read_at ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Lu
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Non lu
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      title="Voir"
                      className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                      onClick={() => handleView(report)}
                    >
                      <EyeIcon className="h-5 w-5 text-gray-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan="6">
                  <div className="flex justify-center items-center py-10 text-gray-500 text-center text-sm">
                    Aucun signalement pour le moment.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />

      <ReportDetailModal
        report={selectedReport}
        onClose={() => setSelectedReport(null)}
      />
    </div>
  );
};

export default ReportList;
